/**
 * Labstep
 */

import { SupportedFormat } from 'ketcher-core';
import type { Indigo } from 'ketcher-core/dist/application/indigo';
import type {
  FetchResult,
  PubChemMetadata,
} from 'labstep-web/containers/PubChem/types';
import bugsnagService from 'labstep-web/services/bugsnag.service';
import ketcherService from 'labstep-web/services/ketcher';
import {
  getInChI,
  identifyStructFormat,
} from 'labstep-web/services/molecule.service';

const width = 'auto';
const height = 200;

export function trimSvg(svg: any) {
  const children = svg.childNodes;
  const toRemove = [];
  for (let i = 0; i < children.length; i += 1) {
    if (
      children[i].localName === 'rect' &&
      children[i].getAttribute('x') === '0' &&
      children[i].getAttribute('y') === '0'
    ) {
      toRemove.push(children[i]);
    }
  }
  toRemove.forEach((child) => svg.removeChild(child));
  const bbox = svg.getBBox();
  const viewBox = [
    bbox.x - 20,
    bbox.y - 20,
    bbox.width + 40,
    bbox.height + 40,
  ].join(' ');
  const trimmedSvg = svg.cloneNode(true);
  trimmedSvg.setAttribute('viewBox', viewBox);
  trimmedSvg.setAttribute('width', width);
  trimmedSvg.setAttribute('height', height);
  return trimmedSvg;
}

export const trimSvgBlob = async (svgBlob: Blob) => {
  let svg = await svgBlob.text();
  svg = svg
    .replace(/<\?xml.+\?>/g, '')
    .replace(/width="(.*?)"/, `width="${width}"`)
    .replace(/height="(.*?)"/, `height="${height}"`);
  return svg;
};

function getNewArrowMode(mode: string) {
  switch (mode) {
    case 'equilibrium':
      return 'equilibrium-filled-half-bow';
    default:
      return 'open-angle';
  }
}

function getArrowPosition(node: any) {
  return [
    {
      x: node.location[0],
      y: node.location[1],
      z: node.location[2],
    },
    {
      x: node.location[0] + 1,
      y: node.location[1],
      z: node.location[2],
    },
  ];
}

function updateNode(node: any) {
  switch (node.type) {
    case 'arrow':
      if (!node.location) {
        return node;
      }
      return {
        type: 'arrow',
        data: {
          pos: getArrowPosition(node),
          mode: getNewArrowMode(node.mode),
        },
      };
    /* Upcoming unreleased change to Ket format
    case 'text':
      if (!node.data || !node.data.position) {
        return node;
      }
      return {
        type: 'text',
        content: node.data.content,
        pos: [node.data.position],
      }; */
    default:
      return node;
  }
}

export function parseKet(data: string) {
  const format = identifyStructFormat(data);
  if (format === SupportedFormat.ket) {
    const oldData = JSON.parse(data);
    const newData = {
      ...oldData,
      root: {
        ...oldData.root,
        nodes: oldData.root.nodes.map(updateNode),
      },
    };
    return JSON.stringify(newData);
  }
  return data;
}

export const fetchMolecularData = async (
  smiles: string,
): Promise<{
  MolecularWeight: number | null;
  MolecularFormula: string | null;
}> => {
  let MolecularWeight = null;
  let MolecularFormula = null;

  try {
    if (ketcherService.isReady()) {
      const result =
        await ketcherService.getChemicalProperties(smiles);
      const chemicalProperties = result[0];
      MolecularWeight = chemicalProperties.mass;
      if (Number.isNaN(Number(MolecularWeight))) {
        MolecularWeight = null;
      }
      MolecularFormula = (
        chemicalProperties.formula as string
      )?.replace(/ /g, '');
    } else {
      console.warn(
        'Ketcher service not ready, skipping molecular data calculation for:',
        smiles,
      );
    }
  } catch (error) {
    console.error(
      'Error calculating molecular data with Ketcher:',
      error,
    );
    bugsnagService.notify(error, {}, 'info');
  }

  return { MolecularWeight, MolecularFormula };
};

export const fetchInChi = async (
  smiles: string,
  indigo?: Indigo,
): Promise<PubChemMetadata['InChI'][]> => {
  const inchis = await getInChI(smiles, indigo);
  return inchis as PubChemMetadata['InChI'][];
};

export const addKetcherPropertiesToMetadata = async (
  metadata: PubChemMetadata,
  indigo?: Indigo,
): Promise<PubChemMetadata> => {
  const smiles = metadata.SMILES as string;

  let inchi: PubChemMetadata['InChI'][] = [];
  let molecularWeight: string | null = null;
  let molecularFormula = null;

  if (!metadata.InChI) {
    try {
      inchi = await fetchInChi(smiles, indigo);
    } catch (e) {
      bugsnagService.notify(e, {}, 'info');
    }
  }

  if (!metadata.MolecularFormula || !metadata.MolecularWeight) {
    try {
      const { MolecularFormula, MolecularWeight } =
        await fetchMolecularData(smiles);
      molecularFormula = MolecularFormula;
      molecularWeight =
        MolecularWeight === null ? null : String(MolecularWeight);
    } catch (e) {
      bugsnagService.notify(e, {}, 'info');
    }
  }

  return {
    ...metadata,
    InChI: metadata.InChI || inchi[0],
    MolecularWeight: metadata.MolecularWeight || molecularWeight,
    MolecularFormula: metadata.MolecularFormula || molecularFormula,
  };
};

export const addKetcherPropertiesToResult = async ({
  reactants,
  products,
  indigo,
}: {
  reactants: PubChemMetadata[];
  products: PubChemMetadata[];
  indigo?: Indigo;
}): Promise<FetchResult> => {
  const fetchMetadataForReactants = Promise.all(
    reactants.map((a) => addKetcherPropertiesToMetadata(a, indigo)),
  );
  const fetchMetadataForProducts = Promise.all(
    products.map((a) => addKetcherPropertiesToMetadata(a, indigo)),
  );

  return await Promise.all([
    fetchMetadataForReactants,
    fetchMetadataForProducts,
  ])
    .then((data) => ({ reactants: data[0], products: data[1] }))
    .catch(() => ({ reactants: [], products: [] }));
};
